import { ArrowLeft, ArrowRight, MousePointerClick, X } from "lucide-react";
import { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router";
import { create } from "zustand";
import { formatAccelerator } from "@ide/shared";
import { isMac } from "@/desktop/bridge";
import { useMe } from "@/lib/queries";
import { cn } from "@/lib/utils";
import { Button } from "./ui/button";
import { InfinityMark } from "./ui/infinity-loader";

/**
 * Tour guiado para quem acabou de criar a conta: a tela escurece e cada passo acende uma parte da
 * interface, com uma seta apontando e o que ela faz. Alguns passos pedem para clicar na parte
 * acesa; "Pular tour" está sempre à mão. Aparece uma vez, na tela de projetos; dá para rever em
 * Ajuda › Tutorial do Kompil. Os passos sem um alvo na tela (o que fica dentro de um projeto)
 * aparecem como um cartão com a imagem da tela.
 */
interface Step {
  title: string;
  text: string;
  /** Elemento aceso: [data-tour="…"]. Sem alvo (ou fora da tela): cartão no centro. */
  target?: string;
  /** Clicar no elemento aceso também avança (o clique faz o que faria normalmente). */
  clickToContinue?: boolean;
  /** Imagem da tela (public/tour/…), para o que fica dentro do projeto. */
  image?: { src: string; alt: string };
  tips?: string[];
}

const k = (keys: string) => formatAccelerator(keys, isMac);

const STEPS: Step[] = [
  {
    title: "Boas-vindas ao Kompil",
    text: "Um tour de um minuto pelas partes principais. Você pode pular quando quiser e rever depois em Ajuda › Tutorial do Kompil.",
  },
  {
    target: "novo-projeto",
    title: "Tudo começa aqui",
    text: "Crie um projeto a partir de um modelo, importe um repositório do GitHub ou uma pasta do seu computador.",
    tips: [`Atalho: ${k("CmdOrCtrl+Alt+N")}`],
  },
  {
    target: "busca",
    title: "Encontre qualquer coisa",
    text: "Arquivos, projetos e comandos num lugar só. Clique aqui para experimentar.",
    clickToContinue: true,
    tips: [`Arquivos e projetos: ${k("CmdOrCtrl+P")}`, `Todos os comandos: ${k("CmdOrCtrl+Shift+P")}`],
  },
  {
    target: "menus",
    title: "Os menus que você já conhece",
    text: "Arquivo, Editar, Ver, Ir, Terminal, Configurações e Ajuda — como no VS Code, com os mesmos atalhos.",
  },
  {
    target: "layout",
    title: "Temas e layout",
    text: "Troque o tema da IDE inteira (editor, terminal e painéis de IA) e mostre ou esconda as barras laterais e o terminal.",
  },
  {
    target: "conta",
    title: "Sua conta, GitHub e IAs",
    text: "No seu perfil você conecta o GitHub (clonar e publicar repositórios) e as suas IAs em Configurações › Conexões de IA.",
    tips: ["A chave da IA fica cifrada só no seu computador"],
  },
  {
    title: "Dentro de um projeto",
    text: "O editor mostra quem está em cada arquivo e o cursor de cada pessoa. Executar (F5) roda o projeto; o terminal fica embaixo; o agente, o Claude e o Copilot ficam na lateral direita — e você revisa tudo o que a IA mudou antes de aprovar.",
    image: { src: "/tour/projeto.png", alt: "O editor de um projeto no Kompil" },
    tips: [`Terminal: ${k("Ctrl+`")}`, "Pontos de parada: clique ao lado do número da linha (F9)"],
  },
  {
    title: "Pronto para começar",
    text: "Mensagens, grupos e a rede de devs estão no menu Ir. Bom código!",
  },
];

const doneKey = (userId: string) => `kompil.tour.done.${userId}`;
/** Contas criadas há menos disto contam como novas. */
const NEW_ACCOUNT_MS = 3 * 24 * 60 * 60_000;
const PAD = 8;

export const useTour = create<{
  open: boolean;
  step: number;
  show(): void;
  hide(): void;
  go(step: number): void;
}>((set) => ({
  open: false,
  step: 0,
  show: () => set({ open: true, step: 0 }),
  hide: () => set({ open: false, step: 0 }),
  go: (step) => set({ step }),
}));

/** Posição do elemento aceso (acompanha redimensionar e rolar). */
function useTargetRect(target: string | undefined) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    let frame = 0;
    const measure = () => {
      const el = target ? document.querySelector<HTMLElement>(`[data-tour="${target}"]`) : null;
      const r = el?.getBoundingClientRect();
      setRect(r && r.width > 0 && r.height > 0 ? r : null);
    };
    frame = window.setTimeout(measure, 0);
    const loop = () => {
      measure();
      frame = window.setTimeout(loop, 250);
    };
    const first = window.setTimeout(loop, 250);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(frame);
      clearTimeout(first);
      window.removeEventListener("resize", measure);
    };
  }, [target]);
  return rect;
}

/** Onde o balão fica em relação ao alvo (embaixo, se couber; senão em cima). */
function placement(rect: DOMRect) {
  const below = rect.bottom + 180 < window.innerHeight;
  const width = 340;
  const left = Math.min(Math.max(12, rect.left + rect.width / 2 - width / 2), window.innerWidth - width - 12);
  const top = below ? rect.bottom + PAD + 46 : Math.max(12, rect.top - PAD - 46 - 200);
  const arrowX = Math.min(Math.max(28, rect.left + rect.width / 2 - left), width - 28);
  return { below, left, top, width, arrowX };
}

export function OnboardingTour() {
  const { data: me } = useMe();
  const { pathname } = useLocation();
  const open = useTour((s) => s.open);
  const step = useTour((s) => s.step);
  const setStep = useTour((s) => s.go);
  const userId = me && !me.user.isGuest ? me.user.id : null;
  const s = STEPS[step];
  const rect = useTargetRect(open ? s?.target : undefined);
  const [imageOk, setImageOk] = useState(true);

  // Conta nova, na tela de projetos, sem ter visto (ou pulado) antes: abre sozinho.
  useEffect(() => {
    if (!userId || !me || pathname !== "/projects") return;
    const fresh = Date.now() - new Date(me.user.createdAt).getTime() < NEW_ACCOUNT_MS;
    let seen = true;
    try {
      seen = localStorage.getItem(doneKey(userId)) === "1";
    } catch {
      // sem armazenamento: não insiste
    }
    if (fresh && !seen) useTour.getState().show();
  }, [userId, me, pathname]);

  const finish = () => {
    if (userId) {
      try {
        localStorage.setItem(doneKey(userId), "1");
      } catch {
        // sem armazenamento: vale só nesta sessão
      }
    }
    useTour.getState().hide();
  };
  const last = step === STEPS.length - 1;
  const next = () => (last ? finish() : setStep(step + 1));

  // Passo "clique aqui": o clique no alvo faz o que faria e avança o tour.
  useEffect(() => {
    if (!open || !s?.target || !s.clickToContinue) return;
    const el = document.querySelector<HTMLElement>(`[data-tour="${s.target}"]`);
    if (!el) return;
    const onClick = () => setTimeout(() => useTour.getState().go(step + 1), 120);
    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, [open, s, step]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") finish();
      if (e.key === "ArrowRight" && !s?.clickToContinue) next();
      if (e.key === "ArrowLeft" && step > 0) setStep(step - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!open || !s) return null;
  const spot = rect
    ? { x: rect.left - PAD, y: rect.top - PAD, w: rect.width + PAD * 2, h: rect.height + PAD * 2 }
    : null;
  const pos = rect ? placement(rect) : null;

  const controls = (
    <div className="mt-4 flex items-center gap-2">
      <div className="flex gap-1.5" aria-hidden>
        {STEPS.map((_, i) => (
          <span
            key={i}
            className={cn(
              "h-1.5 rounded-full transition-all duration-300",
              i === step ? "w-5 bg-fg" : i < step ? "w-1.5 bg-fg/50" : "w-1.5 bg-bg-emphasis",
            )}
          />
        ))}
      </div>
      <div className="ml-auto flex items-center gap-1.5">
        {last ? null : (
          <Button variant="ghost" size="sm" onClick={finish}>
            Pular tour
          </Button>
        )}
        {step > 0 ? (
          <Button variant="secondary" size="sm" onClick={() => setStep(step - 1)} aria-label="Passo anterior">
            <ArrowLeft className="h-3.5 w-3.5" />
          </Button>
        ) : null}
        <Button variant="primary" size="sm" onClick={next} autoFocus>
          {last ? "Começar" : step === 0 ? "Fazer o tour" : "Próximo"}
          {last ? null : <ArrowRight className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );

  const body = (
    <>
      <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-fg-subtle">
        Passo {step + 1} de {STEPS.length}
      </p>
      <h2 className="mt-1 text-[17px] font-semibold tracking-[-0.01em] text-fg">{s.title}</h2>
      <p className="mt-1.5 text-[13px] leading-relaxed text-fg-muted">{s.text}</p>
      {s.tips?.length ? (
        <ul className="mt-2.5 space-y-1">
          {s.tips.map((t) => (
            <li key={t} className="flex items-start gap-2 text-[12px] text-fg">
              <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-fg-subtle" />
              {t}
            </li>
          ))}
        </ul>
      ) : null}
      {s.clickToContinue && spot ? (
        <p className="mt-3 flex items-center gap-1.5 text-[12px] font-medium text-fg">
          <MousePointerClick className="h-3.5 w-3.5 animate-bounce" /> Clique na parte acesa para continuar
        </p>
      ) : null}
      {controls}
    </>
  );

  return createPortal(
    <div className="fixed inset-0 z-[300]" role="dialog" aria-modal="true" aria-label={`Tour: ${s.title}`}>
      {/* Escurece tudo menos o alvo (o alvo continua clicável). */}
      <svg className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden>
        <defs>
          <mask id="kompil-tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {spot ? (
              <rect
                x={spot.x}
                y={spot.y}
                width={spot.w}
                height={spot.h}
                rx="12"
                fill="black"
                style={{ transition: "all 450ms cubic-bezier(.2,.8,.2,1)" }}
              />
            ) : null}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.66)" mask="url(#kompil-tour-mask)" />
      </svg>
      {/* Fora do alvo os cliques não passam (sem cliques perdidos durante o tour). */}
      {spot ? (
        <>
          <div className="absolute inset-x-0 top-0" style={{ height: spot.y }} />
          <div className="absolute inset-x-0 bottom-0" style={{ top: spot.y + spot.h }} />
          <div className="absolute left-0" style={{ top: spot.y, height: spot.h, width: spot.x }} />
          <div className="absolute right-0" style={{ top: spot.y, height: spot.h, left: spot.x + spot.w }} />
          <div
            aria-hidden
            className="pointer-events-none absolute rounded-[12px] border-2 border-white/90 shadow-[0_0_0_6px_rgba(255,255,255,0.12),0_0_40px_rgba(255,255,255,0.25)]"
            style={{
              left: spot.x,
              top: spot.y,
              width: spot.w,
              height: spot.h,
              transition: "all 450ms cubic-bezier(.2,.8,.2,1)",
            }}
          >
            <span className="absolute inset-[-8px] animate-ping-slow rounded-[16px] border border-white/50" />
          </div>
        </>
      ) : (
        <div className="absolute inset-0" />
      )}

      {spot && pos ? (
        <div
          key={step}
          className="absolute animate-fade-up rounded-2xl border border-border bg-bg p-4 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.65)]"
          style={{ left: pos.left, top: pos.top, width: pos.width }}
        >
          {/* Seta curva animada até a parte acesa. */}
          <svg
            aria-hidden
            className={cn("absolute h-10 w-10 text-white", pos.below ? "-top-11" : "-bottom-11 rotate-180")}
            style={{ left: pos.arrowX - 20 }}
            viewBox="0 0 40 40"
            fill="none"
          >
            <path
              d="M20 38 C 20 26, 26 18, 20 4"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              className="kompil-tour-arrow"
              pathLength={100}
            />
            <path
              d="M13 11 L20 3 L27 11"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {body}
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div
            key={step}
            className="w-full max-w-[520px] animate-fade-up rounded-2xl border border-border bg-bg p-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.65)]"
          >
            <button
              type="button"
              onClick={finish}
              aria-label="Fechar o tour"
              className="float-right -mr-2 -mt-2 flex h-7 w-7 items-center justify-center rounded-md text-fg-subtle hover:bg-bg-muted hover:text-fg"
            >
              <X className="h-4 w-4" />
            </button>
            {s.image && imageOk ? (
              <img
                src={s.image.src}
                alt={s.image.alt}
                onError={() => setImageOk(false)}
                className="mb-4 w-full rounded-xl border border-border"
              />
            ) : (
              <div className="mb-4 flex justify-center rounded-xl border border-border bg-bg-subtle py-6">
                <InfinityMark className="h-[56px] w-[112px] text-fg" label={null} />
              </div>
            )}
            {body}
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
